import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OpenJobClock, SearchCriteria, TimeClock, TimeClockEventType } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { Observable } from 'rxjs';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

@Injectable({
    providedIn: 'root',
})
export class TimeClockService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    clockIn(
        tenantId: string,
        branchId: string,
        eventType: TimeClockEventType,
        entityId: string | null = null,
    ): Observable<TimeClock> {
        return this.http.post<TimeClock>(
            `${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}/clock-in?eventType=${eventType}${entityId ? `&entityId=${entityId}` : ''}`,
            {},
            { headers: this.httpHeadersWithNoLoading },
        );
    }

    clockOut(
        tenantId: string,
        branchId: string,
        eventType: TimeClockEventType,
        entityId: string | null = null,
    ): Observable<TimeClock> {
        return this.http.post<TimeClock>(
            `${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}/clock-out?eventType=${eventType}${entityId ? `&entityId=${entityId}` : ''}`,
            {},
            { headers: this.httpHeadersWithNoLoading },
        );
    }

    delete(tenantId: string, branchId: string, id: string): Observable<object> {
        return this.http.delete(`${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}/${id}`);
    }

    isClockedIn(
        tenantId: string,
        branchId: string,
        timeClockEventType: TimeClockEventType,
        entityId: string | null = null,
        showLoading = true,
    ): Observable<boolean> {
        return this.http.get<boolean>(
            `${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}/is-clocked-in/${timeClockEventType}${entityId ? `?entityId=${entityId}` : ''}`,
            { headers: showLoading ? undefined : this.httpHeadersWithNoLoading },
        );
    }

    /**
     * Reads the acting technician's single open job clock-in, or null when they hold none. Called before a
     * job clock-on so the caller can name the job already holding the clock -- the backend's concurrent-job
     * guard rejects the second clock-on without identifying the offending job, and an open job clock is never
     * auto-closed, so it is often one the technician forgot days ago and which is no longer on screen.
     */
    getOpenJobClock(tenantId: string, branchId: string): Observable<OpenJobClock | null> {
        return this.http.get<OpenJobClock | null>(`${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}/open-job-clock`, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    get(tenantId: string, branchId: string, id: string): Observable<TimeClock | null> {
        return this.http.get<TimeClock>(`${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}/${id}`);
    }

    save(tenantId: string, branchId: string, quote: TimeClock): Observable<TimeClock> {
        if (!quote.id || quote.id === null) return this.http.post<TimeClock>(`${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}`, quote);
        else return this.http.put<TimeClock>(`${this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId)}`, quote);
    }

    search(tenantId: string, branchId: string, searchCriteria: SearchCriteria, showLoading = true): Observable<TimeClock[]> {
        return this.http.post<TimeClock[]>(this.serviceUtils.getBaseUrl('timeclock', tenantId, branchId) + '/search', searchCriteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }
}
